import { useEffect, useState } from 'react';
import { getFramework, getPointRect, useGlobalMask, useIdentity, useScale } from '@/framework';
import { ILayerProps, IPoint } from '@/version-types';
import { useDrawEvent } from '../components/draw-pen-path';
import type { EliminatePenPlugin } from '../plugin-instance';
import { useEliminatePenState } from '../states/eliminate-pen-state';
import { ELIMINATE_PEN_EVENT, EliminateMode, EliminateType, IDrawInfo, PenType } from '../types';
import { createPathInfoWithPointConvert, getExtendState } from '../utils';

const useDrawPen = () => {
    const scale = useScale();
    const eliminateConfig = useEliminatePenState()((state) => state.eliminateConfig);
    const { type, penType, penSize } = eliminateConfig;

    // 绘制时设置支持的画笔类型，手动消除笔只支持涂抹
    const drawPenType = type === EliminateType.LEGACY ? PenType.BRUSH : penType;
    // 绘制时候的画笔大小
    const drawPenSize = drawPenType === PenType.BRUSH ? penSize / scale : 32;
    // 画笔光标大小
    const penCursorSize = drawPenType === PenType.BRUSH ? drawPenSize : drawPenSize / scale;

    return {
        drawPenType,
        drawPenSize,
        penCursorSize,
    };
};

const useDrawInfo = () => {
    const identity = useIdentity();
    const framework = getFramework(identity);

    const originLayer = useEliminatePenState()((state) => state.originLayer);
    const setEliminateConfig = useEliminatePenState()((state) => state.setEliminateConfig);

    // 已经绘制完成的所有路径
    const [drawInfos, setDrawInfos] = useState<IDrawInfo[]>([]);
    // 当前正在绘制的路径
    const [drawingInfo, setDrawingInfo] = useState<IDrawInfo | null>(null);

    useEffect(() => {
        const handler = framework.service.flatLayer.onChange(() => {
            if (!originLayer?.id) return;

            const layer = framework.service.flatLayer.getLayer(originLayer.id);

            if (!layer) return;

            const pathInfos = getExtendState(layer);

            console.log('===>pathInfos', pathInfos);
            setDrawInfos(pathInfos);
        });

        const historyHandler = framework.service.history.onBeforeChange(({ data, isRedo, isUndo }) => {
            if (!data || !(isRedo || isUndo)) return;
            if (!originLayer?.id) return;

            const { newLayer } = data;

            if (!newLayer) return;

            const pathInfos = getExtendState(newLayer);

            console.log('===>historyHandler', pathInfos);
            if (pathInfos.length) {
                setEliminateConfig({
                    type: EliminateType.AI,
                });
            }
        });

        return () => {
            framework.service.events.off(handler);
            framework.service.events.off(historyHandler);
        };
    }, [originLayer?.id]);

    return {
        drawInfos,
        drawingInfo,
        setDrawingInfo,
    };
};

/**
 * 无限画布当前不存在 Stage 的概念，因此改成通过检查是否在当前图层上绘制
 * @param identity
 * @param originLayer 当前图层
 * @param e 鼠标事件
 * @returns 是否在当前图层上绘制
 */
function _checkPointInStage(identity: string, originLayer: ILayerProps | null, e: MouseEvent) {
    if (!originLayer) return false;

    const framework = getFramework(identity);

    // TODO: 非无限画布时，需要适配
    if (!framework.service.edgelessCanvas.isEdgelessMode) {
        const stageIndex = framework.service.frame.getActiveIndex();

        return framework.service.transform.checkPointInFrame({ x: e.x, y: e.y }, stageIndex);
    }

    const pointRect = getPointRect(framework.service.transform.getScenePoint(e));
    const layerId = framework.service.transform.tryPickWrap(pointRect, [originLayer]);

    return layerId === originLayer.id;
}

export const useEliminateDraw = () => {
    const identity = useIdentity();
    const framework = getFramework(identity);

    const globalMask = useGlobalMask();
    const isShowLoading = globalMask !== null;

    const eliminateConfig = useEliminatePenState()((state) => state.eliminateConfig);
    const originLayer = useEliminatePenState()((state) => state.originLayer);
    const setEliminateConfig = useEliminatePenState()((state) => state.setEliminateConfig);
    const instance = framework.service.registration.getInstance<EliminatePenPlugin>('EliminatePenPlugin');

    const {
        type,
        manualApply,
        mode,
        selectionType,
        watermarkMaskURL,
        textMaskURL,
        autoRecognizeWatermark,
        autoRecognizeText,
        isWatermarkMaskUsed,
        isTextMaskUsed,
        prompt,
    } = eliminateConfig;

    // 记录画笔指针位置
    const [mousePosition, setMousePosition] = useState<IPoint>({ x: -10000, y: 0 });
    const { drawInfos, drawingInfo, setDrawingInfo } = useDrawInfo();

    const { drawPenType, drawPenSize, penCursorSize } = useDrawPen();

    const handleDrawEnd = async (points: IPoint[]) => {
        if (!originLayer) return;

        if (points.length) {
            const drawPathList = [
                ...drawInfos,
                {
                    penType: drawPenType,
                    penSize: drawPenSize,
                    selectionType,
                    points,
                },
            ];

            framework.service.events.emit(ELIMINATE_PEN_EVENT.DRAW_END);

            // 立即消除不用写入选区记录
            if (!manualApply) {
                const pathInfos = createPathInfoWithPointConvert(originLayer, drawPathList, true);
                await instance.eliminate(pathInfos);
            } else {
                instance.updatePathInfos(drawPathList);
            }
        }
        setDrawingInfo(null);
    };

    const updateDrawingInfo = (points: IPoint[]) => {
        if (points.length) {
            setDrawingInfo({
                penType: drawPenType,
                penSize: drawPenSize,
                selectionType,
                points,
            });
        }
    };

    const checkIsDrawInStage = (e) => {
        // const point = { x: e.x, y: e.y };
        const viewportNode = framework.service.editor.getContainer();

        // ATTENTION: 这里是为了解决出现登录弹窗、订阅弹窗时，在弹窗上点击也会触发绘制的问题
        if (!viewportNode?.contains(e.target as HTMLElement)) {
            return false;
        }

        return _checkPointInStage(identity, originLayer, e);
    };

    useDrawEvent({
        drawPenType,
        disable: isShowLoading,
        // ATTENTION: 这里转换出来的坐标是相对图层的坐标，但矩形选区的绘制需要计算相对视窗的四个点，因此不能在这里进行转换
        // 否则当图片带有旋转时，画出来的矩形也是旋转后的
        convertToLayerPoint: (_, e) => {
            // TODO: 非无限画布时，需要适配
            // if (!checkIsEdgelessMode(identity)) {
            //     return convertToLayerPoint(identity, point, stageIndex);
            // }

            return framework.service.transform.getScenePoint(e);
        },
        isDrawEvent: checkIsDrawInStage,
        // 光标位于画布覆盖层，保留场景坐标；只有选区路径需要转换为图片坐标。
        onMouseMove: setMousePosition,
        onDrawStart: updateDrawingInfo,
        onDrawMove: updateDrawingInfo,
        onDrawEnd: handleDrawEnd,
    });

    useEffect(() => {
        // 从 AI 消除切换到手动消除时，清空未应用的选区
        if (type === EliminateType.LEGACY && drawInfos.length > 0) {
            instance.updateLayerPathInfos([], true);
        }
    }, [type]);

    // 非即涂即消模式下，开始消除按钮控制
    useEffect(() => {
        if (manualApply) {
            const hasRecognize =
                (autoRecognizeWatermark && !!watermarkMaskURL && !isWatermarkMaskUsed) ||
                (autoRecognizeText && !!textMaskURL && !isTextMaskUsed);

            const canModify = mode === EliminateMode.MODIFY && prompt.trim().length > 0;

            setEliminateConfig({ canApply: drawInfos.length > 0 || hasRecognize || canModify });
        }
    }, [
        drawInfos.length,
        manualApply,
        watermarkMaskURL,
        textMaskURL,
        autoRecognizeWatermark,
        autoRecognizeText,
        isWatermarkMaskUsed,
        isTextMaskUsed,
        mode,
        prompt,
    ]);

    return {
        drawPenType,
        penCursorSize,
        penVisible: !isShowLoading,
        mousePosition,
        drawingPath: drawingInfo && originLayer ? createPathInfoWithPointConvert(originLayer, [drawingInfo])[0] : null,
        drawPathList: originLayer ? createPathInfoWithPointConvert(originLayer, drawInfos, true) : [],
    };
};
